import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, MessageSquarePlus, Search, Users, UsersRound } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Avatar } from '@/shared/ui/Avatar';
import { IconButton } from '@/shared/ui/IconButton';
import { useAuthStore } from '@/entities/session/model/authStore';
import { useUserPhoto } from '@/features/presence/model/presenceHooks';
import { useChatStore } from '../model/chatStore';
import type { ChatContact, ChatConversation } from '../model/types';
import { listTime } from './chatFormat';
import { NewGroupDialog } from './GroupDialogs';

function previewOf(c: ChatConversation, myId: string, t: (k: string) => string) {
  const m = c.lastMessage;
  if (!m) return t('chat.noMessagesYet');
  const body =
    m.type === 'deleted' ? t('chat.deleted') : m.type === 'image' ? `📷 ${t('chat.photo')}` : m.type === 'share' ? `🔗 ${m.content || t('chat.sharedScreen')}` : m.content;
  const mine = m.senderId.toLowerCase() === myId.toLowerCase();
  if (mine) return `${t('chat.you')}: ${body}`;
  if (c.kind === 'group' && m.senderName) return `${m.senderName}: ${body}`;
  return body;
}

function PersonAvatar({ userId, name, online }: { userId: string; name: string; online?: boolean }) {
  const photo = useUserPhoto(userId);
  return (
    <span className="relative shrink-0">
      <Avatar name={name || '?'} imageBase64={photo} className="h-10 w-10" />
      {online ? <span className="ring-bg-elevated absolute right-0 bottom-0 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2" /> : null}
    </span>
  );
}

function Row({ c, active, onOpen }: { c: ChatConversation; active: boolean; onOpen: () => void }) {
  const { t, i18n } = useTranslation();
  const myId = useAuthStore((s) => s.user?.userId ?? '');
  // Seçici yeni dizi döndürmemeli (her okumada farklı referans → sonsuz render); yalnız bayrak.
  const typing = useChatStore((s) => (s.typing[c.key] ?? []).some((x) => x.until > Date.now()));
  const online = useChatStore((s) => (c.peerUserId ? s.contacts.find((x) => x.userId === c.peerUserId)?.isOnline : false));
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn('flex w-full items-center gap-2.5 px-3 py-2 text-left', active ? 'bg-brand-500/10' : 'hover:bg-bg-hover')}
    >
      {c.kind === 'group' ? (
        <span className="bg-brand-500/15 text-brand-600 flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
          <Users className="h-5 w-5" />
        </span>
      ) : (
        <PersonAvatar userId={c.peerUserId!} name={c.title ?? ''} online={online} />
      )}
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className={cn('min-w-0 flex-1 truncate text-sm', c.unread ? 'font-semibold' : 'font-medium')}>{c.title}</span>
          {c.lastMessage ? (
            <span className={cn('shrink-0 text-[11px] tabular-nums', c.unread ? 'text-brand-600 font-semibold' : 'text-fg-subtle')}>
              {listTime(c.lastMessage.sentAt, i18n.language)}
            </span>
          ) : null}
        </span>
        <span className="flex items-center gap-2">
          <span className={cn('min-w-0 flex-1 truncate text-xs', typing ? 'text-brand-600' : c.unread ? 'text-fg' : 'text-fg-muted')}>
            {typing ? t('chat.typing') : previewOf(c, myId, t)}
          </span>
          {c.unread ? (
            <span className="bg-brand-600 min-w-5 shrink-0 rounded-full px-1.5 text-center text-[10px] leading-5 font-semibold text-white tabular-nums">
              {c.unread > 99 ? '99+' : c.unread}
            </span>
          ) : null}
        </span>
      </span>
    </button>
  );
}

function ContactItem({ c, onOpen }: { c: ChatContact; onOpen: () => void }) {
  return (
    <button type="button" onClick={onOpen} className="hover:bg-bg-hover flex w-full items-center gap-2.5 px-3 py-2 text-left">
      <PersonAvatar userId={c.userId} name={c.userName} online={c.isOnline} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{c.userName}</span>
        <span className="text-fg-subtle block truncate text-xs">{c.roleName ?? c.userCode}</span>
      </span>
    </button>
  );
}

export function ConversationList({ onOpen }: { onOpen: (key: string) => void }) {
  const { t } = useTranslation();
  const conversations = useChatStore((s) => s.conversations);
  const loaded = useChatStore((s) => s.conversationsLoaded);
  const contacts = useChatStore((s) => s.contacts);
  const activeKey = useChatStore((s) => s.activeKey);
  const loadContacts = useChatStore((s) => s.loadContacts);
  const [q, setQ] = useState('');
  const [mode, setMode] = useState<'list' | 'people'>('list');
  const [groupOpen, setGroupOpen] = useState(false);

  // Çevrimiçi durumları ve "yeni sohbet" listesi için kişiler bir kez, sonra dakikada bir.
  useEffect(() => {
    void loadContacts();
    const id = setInterval(() => void loadContacts(), 60_000);
    return () => clearInterval(id);
  }, [loadContacts]);

  const term = q.trim().toLocaleLowerCase('tr');
  const shownConvs = useMemo(
    () => conversations.filter((c) => !term || (c.title ?? '').toLocaleLowerCase('tr').includes(term)),
    [conversations, term],
  );
  const shownPeople = useMemo(
    () => contacts.filter((c) => !term || `${c.userName} ${c.userCode} ${c.roleName ?? ''}`.toLocaleLowerCase('tr').includes(term)),
    [contacts, term],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-1 p-2">
        {mode === 'people' ? (
          <IconButton label={t('chat.back')} size="sm" onClick={() => setMode('list')}>
            <ArrowLeft className="h-4 w-4" />
          </IconButton>
        ) : null}
        <div className="border-border bg-bg flex min-w-0 flex-1 items-center gap-1.5 rounded-full border px-3">
          <Search className="text-fg-subtle h-3.5 w-3.5 shrink-0" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={mode === 'people' ? t('chat.searchPeople') : t('chat.search')}
            className="h-8 min-w-0 flex-1 bg-transparent text-sm outline-none"
          />
        </div>
        {mode === 'list' ? (
          <>
            <IconButton label={t('chat.newChat')} size="sm" onClick={() => setMode('people')}>
              <MessageSquarePlus className="h-4 w-4" />
            </IconButton>
            <IconButton label={t('chat.newGroup')} size="sm" onClick={() => setGroupOpen(true)}>
              <UsersRound className="h-4 w-4" />
            </IconButton>
          </>
        ) : null}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {mode === 'people' ? (
          shownPeople.length ? (
            shownPeople.map((c) => (
              <ContactItem
                key={c.userId}
                c={c}
                onOpen={() => {
                  setMode('list');
                  setQ('');
                  onOpen(`u:${c.userId}`);
                }}
              />
            ))
          ) : (
            <p className="text-fg-subtle px-6 py-10 text-center text-sm">{t('chat.noPeople')}</p>
          )
        ) : shownConvs.length ? (
          shownConvs.map((c) => <Row key={c.key} c={c} active={c.key === activeKey} onOpen={() => onOpen(c.key)} />)
        ) : loaded ? (
          <div className="text-fg-subtle flex flex-col items-center gap-3 px-6 py-10 text-center text-sm">
            <span>{term ? t('chat.noResults') : t('chat.noConversations')}</span>
            {!term ? (
              <button type="button" className="text-brand-600 font-medium hover:underline" onClick={() => setMode('people')}>
                {t('chat.startChat')}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      {groupOpen ? (
        <NewGroupDialog
          onClose={() => setGroupOpen(false)}
          onCreated={(key) => {
            setGroupOpen(false);
            onOpen(key);
          }}
        />
      ) : null}
    </div>
  );
}
